import { GroupListComponent } from './GroupList.types';
import { useContext, useEffect, useMemo, useState } from 'react';
import { NewPageWrapper, RichTable, useTitle, getApiConfiguration } from '../../common';
import { GroupDto, GroupsApi } from '../../api-client';
import { NotificationsContext } from '../../common/notifications/Notifications';
import { useGetGroups } from './hooks/useGetGroups';
import { TableData } from '@hiiretail/synergy-ui/dist/types/components/surfaces/Table/Table.types';
import { CreateGroupModal } from './modals/CreateGroupModal/CreateGroupModal';
import { useCreateGroup } from './hooks/useCreateGroup';
import { ErrorResponse } from '../../common/types/errorRespose';
import { useSearch } from '../../common/hooks/useSearch/useSearch';
import { FuseOptions } from 'fuse.js';
import { useBreadcrumbsActions } from '../../common/components/Breadcrumbs/context/useBreadcrumbsActions';
import { Flexbox } from '@hiiretail/synergy-ui';
import { RefreshButton } from '../../common/components/Breadcrumbs/buttons/RefreshButton/RefreshButton';
import { CreateButton } from '../../common/components/Breadcrumbs/buttons/CreateButton/CreateButton';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { Status } from '../../common/components/Status/Status';

export const GroupList: GroupListComponent = () => {
  useTitle('Groups');
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const groupsApi = new GroupsApi(getApiConfiguration());
  const queryClient = useQueryClient();
  const { showNotification } = useContext(NotificationsContext);
  const history = useHistory();

  const { updateActions } = useBreadcrumbsActions();

  useEffect(() => {
    updateActions(
      <>
        <Flexbox item>
          <RefreshButton onClick={refetchGroups} permission="iam.group.list" />
        </Flexbox>
        <Flexbox item>
          <CreateButton
            title="New group"
            onClick={toggleCreateGroupDialogOpen}
            permission="iam.group.create"
          />
        </Flexbox>
      </>,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: groups,
    isFetching: isLoading,
    error: fetchError,
    refetch: refetchGroups,
  } = useGetGroups(groupsApi, localStorage.getItem('tenantId') as string, () => void 0);

  const {
    mutate: createGroup,
    isLoading: isCreating,
    error: createError,
  } = useCreateGroup(
    groupsApi,
    localStorage.getItem('tenantId') as string,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    () => {},
    ({
      data: { name },
    }: {
      data: {
        name: string;
      };
    }) => {
      queryClient.invalidateQueries(['groups']).then();
      showNotification(`User Group ${name} created successfully`);
      toggleCreateGroupDialogOpen();
    },
  );

  const searchOptions: FuseOptions<GroupDto> = useMemo(
    () => ({
      keys: ['name', 'description'],
      caseSensitive: false,
      findAllMatches: true,
      threshold: 0.1,
    }),
    [],
  );

  const { filteredData: filteredGroups, onSearchChange } = useSearch(
    groups as GroupDto[],
    searchOptions,
  );

  const tableColumns = useMemo(
    () => [
      { Header: 'Name', accessor: 'name' },
      { Header: 'Description', accessor: 'description' },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ row: { original } }: { row: { original: GroupDto } }) => (
          <Status status={original.status} variant="caption" />
        ),
      },
    ],
    [],
  );

  const handleRowClick = (row: TableData) => history.push(`/groups/${row.id}`);

  const toggleCreateGroupDialogOpen = () => {
    setIsCreateGroupModalOpen((prevVal) => !prevVal);
  };

  return (
    <NewPageWrapper>
      <CreateGroupModal
        onClose={toggleCreateGroupDialogOpen}
        isOpen={isCreateGroupModalOpen}
        isCreating={isCreating}
        error={createError}
        createGroup={createGroup}
      />
      <RichTable
        id="groups-table"
        title="Browse groups"
        columns={tableColumns}
        data={filteredGroups as unknown as TableData[]}
        isLoading={isLoading}
        searchFieldLabel="Filter by name or description"
        onSearchChange={onSearchChange}
        fetchError={fetchError as ErrorResponse | null}
        refetch={refetchGroups}
        errorSubject="groups"
        permissions={{
          create: 'iam.group.create',
          list: 'iam.group.list',
        }}
        onRowClick={handleRowClick}
      />
    </NewPageWrapper>
  );
};
